import React from 'react';
import { ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import type { NavigationItem } from '../navigationItems';
import { useNavItemVisible } from '../../../hooks/useNavItemVisible';
import { useSidebarDensity } from './constants';

interface SidebarNavItemProps {
  item: NavigationItem;
  permissions: string[];
  isActive: boolean;
  onNavigate: (item: NavigationItem) => void;
}

export const SidebarNavItem: React.FC<SidebarNavItemProps> = ({
  item,
  permissions,
  isActive,
  onNavigate,
}) => {
  const visible = useNavItemVisible(item, permissions);
  const { navItemSx, iconSx, ICON_SIZE } = useSidebarDensity();
  if (!visible) return null;

  const IconComponent = item.icon;
  const isHighlight = item.highlight && !isActive;

  return (
    <ListItemButton
      onClick={() => onNavigate(item)}
      data-testid={`expanded-subnav-${item.text.toLowerCase().replace(/\s+/g, '-')}`}
      sx={{
        ...navItemSx,
        color: isActive
          ? 'primary.main'
          : isHighlight
            ? 'primary.main'
            : 'text.secondary',
        bgcolor: isActive
          ? 'action.selected'
          : isHighlight
            ? 'rgba(240, 183, 38, 0.08)'
            : 'transparent',
        borderLeft: isHighlight ? '2px solid' : '2px solid transparent',
        borderColor: isHighlight ? 'primary.main' : 'transparent',
        '&:hover': {
          bgcolor: isHighlight ? 'rgba(240, 183, 38, 0.14)' : 'action.hover',
          color: isHighlight ? 'primary.main' : 'text.primary',
        },
      }}
    >
      <ListItemIcon
        sx={{
          ...iconSx,
          color: isActive
            ? 'primary.main'
            : isHighlight
              ? 'primary.main'
              : 'text.secondary',
        }}
      >
        <IconComponent sx={{ fontSize: ICON_SIZE }} />
      </ListItemIcon>
      <ListItemText
        primary={item.text}
        primaryTypographyProps={{
          fontWeight: isActive || isHighlight ? 600 : 400,
          variant: 'body2',
        }}
      />
    </ListItemButton>
  );
};
